import React from "react";
import { maskCep } from "../../utils/masks";

const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];

/**
 * Seções de campos específicos por tipo de bem (Veículo / Imóvel).
 * Renderiza nada para os demais tipos.
 *
 * Props:
 * - tipo: valor do enum TipoPatrimonio selecionado
 * - formData / setFormData: estado do formulário (contém os campos específicos)
 * - disabled: desabilita os inputs (sem permissão de edição)
 */
const CamposEspecificosTipo = ({ tipo, formData, setFormData, disabled = false }) => {
    const inputClass = "px-3 py-2 border border-slate-300 rounded-md text-sm w-full";
    const labelClass = "text-sm text-slate-700 font-medium";

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    if (tipo === "VEICULO") {
        return (
            <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                <div className="text-stone-600 text-base font-medium">Dados do Veículo</div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Placa</label>
                        <input
                            type="text"
                            name="placa"
                            value={formData.placa}
                            onChange={(e) => setFormData((prev) => ({ ...prev, placa: e.target.value.toUpperCase() }))}
                            className={inputClass}
                            placeholder="ABC1D23"
                            maxLength={8}
                            disabled={disabled}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Renavam</label>
                        <input type="text" name="renavam" value={formData.renavam} onChange={handleChange} className={inputClass} maxLength={11} disabled={disabled} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Chassi</label>
                        <input type="text" name="chassi" value={formData.chassi} onChange={handleChange} className={inputClass} maxLength={17} disabled={disabled} />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Ano de Fabricação</label>
                        <input type="number" min="1900" max="2100" name="anoFabricacao" value={formData.anoFabricacao} onChange={handleChange} className={inputClass} placeholder="Ex: 2022" disabled={disabled} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Ano do Modelo</label>
                        <input type="number" min="1900" max="2100" name="anoModelo" value={formData.anoModelo} onChange={handleChange} className={inputClass} placeholder="Ex: 2023" disabled={disabled} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Quilometragem (km)</label>
                        <input type="number" min="0" name="quilometragem" value={formData.quilometragem} onChange={handleChange} className={inputClass} placeholder="Ex: 45000" disabled={disabled} />
                    </div>
                </div>
            </div>
        );
    }

    if (tipo === "SOFTWARE") {
        return (
            <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                <div className="text-stone-600 text-base font-medium">Dados da Licença</div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1.5 md:col-span-2">
                        <label className={labelClass}>Chave / Serial da licença</label>
                        <input
                            type="text"
                            name="chaveLicenca"
                            value={formData.chaveLicenca}
                            onChange={handleChange}
                            className={inputClass}
                            placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                            disabled={disabled}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Licenciamento</label>
                        <select name="tipoLicenciamento" value={formData.tipoLicenciamento} onChange={handleChange} className={inputClass} disabled={disabled}>
                            <option value="">Selecionar</option>
                            <option value="PERPETUA">Perpétua</option>
                            <option value="ASSINATURA">Assinatura</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Validade / Renovação</label>
                        <input
                            type="date"
                            name="validadeLicenca"
                            value={formData.validadeLicenca}
                            onChange={handleChange}
                            className={inputClass}
                            disabled={disabled}
                        />
                        <div className="text-slate-400 text-xs">
                            Licenças com validade próxima aparecem na tela de Alertas.
                        </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Nº de ativações/usuários</label>
                        <input
                            type="number"
                            min="0"
                            name="qtdAtivacoes"
                            value={formData.qtdAtivacoes}
                            onChange={handleChange}
                            className={inputClass}
                            placeholder="Ex: 5"
                            disabled={disabled}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Conta / E-mail vinculado</label>
                        <input
                            type="text"
                            name="contaVinculada"
                            value={formData.contaVinculada}
                            onChange={handleChange}
                            className={inputClass}
                            placeholder="ti@dafmc.com.br"
                            disabled={disabled}
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (tipo === "IMOVEL") {
        return (
            <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                <div className="text-stone-600 text-base font-medium">Dados do Imóvel</div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1.5 md:col-span-2">
                        <label className={labelClass}>Endereço</label>
                        <input type="text" name="endereco" value={formData.endereco} onChange={handleChange} className={inputClass} placeholder="Rua, número, bairro" disabled={disabled} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>CEP</label>
                        <input
                            type="text"
                            name="cep"
                            value={formData.cep}
                            onChange={(e) => setFormData((prev) => ({ ...prev, cep: maskCep(e.target.value) }))}
                            className={inputClass}
                            placeholder="00000-000"
                            maxLength={9}
                            disabled={disabled}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1.5 md:col-span-2">
                        <label className={labelClass}>Município</label>
                        <input type="text" name="municipio" value={formData.municipio} onChange={handleChange} className={inputClass} disabled={disabled} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>UF</label>
                        <select name="uf" value={formData.uf} onChange={handleChange} className={inputClass} disabled={disabled}>
                            <option value="">Selecionar</option>
                            {UFS.map((sigla) => (
                                <option key={sigla} value={sigla}>{sigla}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Matrícula do Imóvel</label>
                        <input type="text" name="matriculaImovel" value={formData.matriculaImovel} onChange={handleChange} className={inputClass} placeholder="Nº da matrícula no cartório" disabled={disabled} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Inscrição Municipal / IPTU</label>
                        <input type="text" name="inscricaoMunicipal" value={formData.inscricaoMunicipal} onChange={handleChange} className={inputClass} disabled={disabled} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className={labelClass}>Área (m²)</label>
                        <input type="number" min="0" step="0.01" name="areaM2" value={formData.areaM2} onChange={handleChange} className={inputClass} placeholder="Ex: 120.5" disabled={disabled} />
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export const CAMPOS_ESPECIFICOS_INICIAIS = {
    placa: "",
    renavam: "",
    chassi: "",
    anoFabricacao: "",
    anoModelo: "",
    quilometragem: "",
    endereco: "",
    cep: "",
    municipio: "",
    uf: "",
    matriculaImovel: "",
    inscricaoMunicipal: "",
    areaM2: "",
    chaveLicenca: "",
    tipoLicenciamento: "",
    validadeLicenca: "",
    qtdAtivacoes: "",
    contaVinculada: "",
};

/** Anexa os campos específicos ao FormData de envio. */
export const appendCamposEspecificos = (data, formData) => {
    Object.keys(CAMPOS_ESPECIFICOS_INICIAIS).forEach((campo) => {
        data.append(campo, formData[campo] ?? "");
    });
};

export default CamposEspecificosTipo;
